import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Loader2, Rocket, UserPlus, Download, Lock, CheckCircle2, RotateCcw } from 'lucide-react';
import { api } from '@/lib/api';
import { tpError } from '@/lib/tpErrors';
import { isActiveCollaborator } from '@/lib/employment';
import { toast } from 'sonner';
import { PerfEvaluationDetail } from '@/components/client/PerfEvaluationDetail';

const FRONTS = ['self', 'supervisor', 'internal_client'];

const STATUS_STYLE = {
  draft: 'bg-muted text-muted-foreground',
  active: 'bg-info/10 text-info border-info/20',
  closed: 'bg-success/10 text-success border-success/20',
};

export function PerfCycleDetail({ cycleId, onBack }) {
  const { t, i18n } = useTranslation();
  const es = i18n.language?.startsWith('es');
  const [cycle, setCycle] = useState(null);
  const [instruments, setInstruments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [detailEval, setDetailEval] = useState(null);
  const [form, setForm] = useState({ subject: '', supervisor: '', internal: [] });

  const load = useCallback(() => {
    api.get(`/client/talentperformance/cycles/${cycleId}`).then((r) => setCycle(r.data))
      .catch((e) => { toast.error(tpError(t, e)); onBack(); });
  }, [cycleId, onBack, t]);

  useEffect(() => {
    load();
    api.get('/client/talentperformance/instruments').then((r) => setInstruments(r.data)).catch(() => {});
    api.get('/client/employees').then((r) => {
      const list = r.data?.items || r.data || [];
      setEmployees(list.filter(isActiveCollaborator));
    }).catch(() => setEmployees([]));
  }, [load]);

  if (!cycle) return <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>;
  const draft = cycle.status === 'draft';

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); load(); }
    catch (e) { toast.error(tpError(t, e)); }
    finally { setBusy(false); }
  };

  const patchFront = (f, patch) => {
    const fronts = {};
    FRONTS.forEach((k) => {
      const cur = cycle.fronts?.[k] || {};
      fronts[k] = { enabled: cur.enabled, questionnaire_id: cur.questionnaire_id, weight: cur.weight || 0 };
    });
    fronts[f] = { ...fronts[f], ...patch };
    return act(() => api.patch(`/client/talentperformance/cycles/${cycleId}`, {
      name: cycle.name, description: cycle.description,
      period_start: cycle.period_start, period_end: cycle.period_end, due_date: cycle.due_date,
      fronts,
    }));
  };

  const addEmployee = () => act(async () => {
    await api.post(`/client/talentperformance/cycles/${cycleId}/evaluations`, {
      subject_user_id: form.subject,
      supervisor_user_id: form.supervisor || null,
      internal_evaluator_ids: form.internal,
    });
    setAddOpen(false);
    setForm({ subject: '', supervisor: '', internal: [] });
  }, t('talentperformance.cycle.employeeAdded', { defaultValue: 'Employee added to the cycle.' }));

  const weightsSum = FRONTS.reduce((s, f) => s + ((cycle.fronts?.[f]?.enabled && cycle.fronts?.[f]?.weight > 0) ? cycle.fronts[f].weight : 0), 0);
  const notIn = (ids) => employees.filter((u) => !ids.includes(u.id));

  return (
    <div className="space-y-5" data-testid="tp-cycle-detail">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5" data-testid="tp-cycle-back">
          <ArrowLeft className="h-4 w-4" />{t('common.back', { defaultValue: 'Back' })}
        </Button>
        <h3 className="text-lg font-semibold">{cycle.name}</h3>
        <Badge variant="outline" className={STATUS_STYLE[cycle.status]}>{t(`talentperformance.status.${cycle.status}`)}</Badge>
        {cycle.status === 'active' && (
          <Button size="sm" variant="outline" className="gap-1.5 ml-auto" disabled={busy}
            onClick={() => act(() => api.post(`/client/talentperformance/cycles/${cycleId}/close`), t('talentperformance.cycle.closedOk', { defaultValue: 'Cycle closed.' }))}
            data-testid="tp-close-cycle">
            <CheckCircle2 className="h-3.5 w-3.5" />{t('talentperformance.cycle.close', { defaultValue: 'Close cycle' })}
          </Button>
        )}
        {cycle.status === 'closed' && (
          <Button size="sm" variant="ghost" className="gap-1.5 ml-auto" disabled={busy}
            onClick={() => {
              const note = window.prompt(t('talentperformance.cycle.reopenNote', { defaultValue: 'Reopening is audited — explain why:' }));
              if (note && note.trim().length >= 5) act(() => api.post(`/client/talentperformance/cycles/${cycleId}/reopen`, { note: note.trim() }));
            }} data-testid="tp-reopen-cycle">
            <RotateCcw className="h-3.5 w-3.5" />{t('talentperformance.cycle.reopen', { defaultValue: 'Reopen' })}
          </Button>
        )}
      </div>

      {/* Fronts configuration */}
      <Card className="p-4 space-y-3 shadow-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h4 className="text-sm font-semibold">{t('talentperformance.cycle.frontsTitle', { defaultValue: 'Evaluation fronts, instruments and weighting' })}</h4>
          {draft && (
            <Badge variant="outline" className={weightsSum === 100 ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'} data-testid="tp-weights-sum">
              {t('talentperformance.cycle.weightsTotal', { defaultValue: 'Weighted total' })}: {weightsSum}%
            </Badge>
          )}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {FRONTS.map((f) => {
            const cfg = cycle.fronts?.[f] || {};
            return (
              <div key={f} className={`rounded-lg border p-3 space-y-2 ${cfg.enabled ? 'border-primary/30' : 'border-border/60 opacity-60'}`} data-testid={`tp-front-cfg-${f}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{t(`talentperformance.fronts.${f}`)}</p>
                  {draft && (
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" disabled={busy}
                      onClick={() => patchFront(f, { enabled: !cfg.enabled })} data-testid={`tp-front-toggle-${f}`}>
                      {cfg.enabled ? t('common.disable', { defaultValue: 'Disable' }) : t('common.enable', { defaultValue: 'Enable' })}
                    </Button>
                  )}
                </div>
                {cfg.enabled && (
                  <>
                    {draft ? (
                      <Select value={cfg.questionnaire_id || ''} onValueChange={(v) => patchFront(f, { questionnaire_id: v })}>
                        <SelectTrigger className="w-full min-w-0 [&>span]:truncate h-8 text-xs" data-testid={`tp-front-instrument-${f}`}>
                          <SelectValue placeholder={t('talentperformance.cycle.selectInstrument', { defaultValue: 'Select instrument' })} />
                        </SelectTrigger>
                        <SelectContent>
                          {instruments.map((q) => <SelectItem key={q.id} value={q.id}>{es ? q.name : (q.name_en || q.name)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-xs text-muted-foreground truncate">{cfg.title || '—'}</p>
                    )}
                    <div className="flex items-center gap-2">
                      <Label className="text-xs shrink-0">{t('talentperformance.cycle.weight', { defaultValue: 'Weight' })}</Label>
                      {draft ? (
                        <Input type="number" min={0} max={100} value={cfg.weight ?? 0} className="h-8 w-20 text-xs"
                          onChange={(e) => patchFront(f, { weight: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
                          data-testid={`tp-front-weight-${f}`} />
                        ) : null}
                      {draft ? <span className="text-xs text-muted-foreground">%</span>
                        : <Badge variant="outline" data-testid={`tp-front-weight-badge-${f}`}>{cfg.weight}%</Badge>}
                    </div>
                    {f === 'self' && cfg.weight === 0 && (
                      <p className="text-[11px] text-muted-foreground">{t('talentperformance.cycle.selfZeroHint', { defaultValue: 'At 0% the self-evaluation stays qualitative and does not affect the weighted result.' })}</p>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
        {draft && (
          <Button className="gap-1.5" disabled={busy}
            onClick={() => act(() => api.post(`/client/talentperformance/cycles/${cycleId}/activate`), t('talentperformance.cycle.activatedOk', { defaultValue: 'Cycle activated — evaluations sent to the portal.' }))}
            data-testid="tp-activate-cycle">
            <Rocket className="h-4 w-4" />{t('talentperformance.cycle.activate', { defaultValue: 'Activate cycle and send evaluations' })}
          </Button>
        )}
      </Card>

      {/* Employees */}
      <Card className="shadow-card overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between flex-wrap gap-2">
          <h4 className="text-sm font-semibold">{t('talentperformance.cycle.employees', { defaultValue: 'Evaluated employees' })} ({(cycle.evaluations || []).length})</h4>
          <div className="flex gap-2">
            {cycle.status !== 'draft' && (
              <Button size="sm" variant="outline" className="gap-1.5"
                onClick={async () => {
                  try {
                    const r = await api.get(`/client/talentperformance/cycles/${cycleId}/export/csv`, { params: { lang: es ? 'es' : 'en' }, responseType: 'blob' });
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(r.data); a.download = 'cycle-results.csv'; a.click();
                  } catch (e) { toast.error(tpError(t, e)); }
                }} data-testid="tp-export-csv">
                <Download className="h-3.5 w-3.5" /> CSV
              </Button>
            )}
            {cycle.status !== 'closed' && (
              <Button size="sm" className="gap-1.5" onClick={() => setAddOpen(true)} data-testid="tp-add-employee">
                <UserPlus className="h-3.5 w-3.5" />{t('talentperformance.cycle.addEmployee', { defaultValue: 'Add employee' })}
              </Button>
            )}
          </div>
        </div>
        {(cycle.evaluations || []).length === 0 ? (
          <p className="p-8 text-sm text-muted-foreground text-center" data-testid="tp-evals-empty">{t('talentperformance.cycle.noEmployees', { defaultValue: 'No employees in this cycle yet.' })}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[680px]">
              <thead>
                <tr className="border-b bg-muted/40 text-left text-[12px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2.5 font-medium">{t('talentperformance.fields.employee', { defaultValue: 'Employee' })}</th>
                  <th className="px-4 py-2.5 font-medium">{t('talentperformance.fields.supervisor', { defaultValue: 'Supervisor' })}</th>
                  <th className="px-4 py-2.5 font-medium">{t('talentperformance.fields.internalClients', { defaultValue: 'Internal clients' })}</th>
                  <th className="px-4 py-2.5 font-medium">{t('talentperformance.fields.progress', { defaultValue: 'Progress' })}</th>
                  <th className="px-4 py-2.5 font-medium">{t('talentperformance.fields.status', { defaultValue: 'Status' })}</th>
                </tr>
              </thead>
              <tbody>
                {(cycle.evaluations || []).map((e) => (
                  <tr key={e.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer" onClick={() => setDetailEval(e.id)} data-testid={`tp-eval-row-${e.id}`}>
                    <td className="px-4 py-2.5 font-medium">{e.snapshot?.user_name}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{e.supervisor_name || '—'}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{(e.internal_evaluators || []).length}</td>
                    <td className="px-4 py-2.5">{e.fronts_total ? `${e.fronts_submitted}/${e.fronts_total}` : '—'}</td>
                    <td className="px-4 py-2.5"><Badge variant="outline">{t(`talentperformance.evalStatus.${e.status}`, { defaultValue: e.status })}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Add-employee dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="tp-add-dialog">
          <DialogHeader>
            <DialogTitle>{t('talentperformance.cycle.addEmployee', { defaultValue: 'Add employee' })}</DialogTitle>
            <DialogDescription>{t('talentperformance.cycle.addDesc', { defaultValue: 'Designate the supervisor and the internal-client evaluators for this employee.' })}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>{t('talentperformance.fields.employee', { defaultValue: 'Employee' })}</Label>
              <Select value={form.subject} onValueChange={(v) => setForm((f0) => ({ ...f0, subject: v }))}>
                <SelectTrigger className="w-full [&>span]:truncate" data-testid="tp-subject-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{employees.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>{t('talentperformance.fields.supervisor', { defaultValue: 'Supervisor' })}</Label>
              <Select value={form.supervisor} onValueChange={(v) => setForm((f0) => ({ ...f0, supervisor: v }))}>
                <SelectTrigger className="w-full [&>span]:truncate" data-testid="tp-supervisor-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{notIn([form.subject]).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>{t('talentperformance.fields.internalClients', { defaultValue: 'Internal clients' })}</Label>
              <div className="max-h-40 overflow-y-auto rounded border border-border/60 p-2 space-y-1" data-testid="tp-internal-picker">
                {notIn([form.subject]).map((u) => (
                  <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.internal.includes(u.id)}
                      onChange={(ev) => setForm((f0) => ({
                        ...f0,
                        internal: ev.target.checked ? [...f0.internal, u.id] : f0.internal.filter((x) => x !== u.id),
                      }))} />
                    <span className="truncate">{u.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button disabled={busy || !form.subject} onClick={addEmployee} data-testid="tp-add-submit">
              {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{t('common.add', { defaultValue: 'Add' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {detailEval && (
        <PerfEvaluationDetail cycleId={cycleId} evaluationId={detailEval}
          cycleStatus={cycle.status} onClose={() => { setDetailEval(null); load(); }} />
      )}
    </div>
  );
}
